import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { apiClient } from '../services/api'

interface Webhook {
    Id: string
    WebhookTypeId: number
    WebhookTypeName: string
    WebhookTypeDescription: string
    WebhookUrl: string
    ApiKey: string | null
    IsActive: boolean
}

interface Broker {
    id: string
    name: string
    address1: string | null
    address2: string | null
    city: string | null
    state: string | null
    postalCode: string | null
    country: string | null
    contactFirstName: string | null
    contactLastName: string | null
    contactEmail: string | null
    contactPhone: string | null
    isActive: boolean
    webhooks: Webhook[]
}

interface WebhookType {
    Id: number
    Name: string
    Description: string
}

export default function BrokerDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [broker, setBroker] = useState<Broker | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Edit Broker Modal
    const [editBrokerOpen, setEditBrokerOpen] = useState(false)
    const [editBrokerData, setEditBrokerData] = useState<Partial<Broker>>({})
    const [savingBroker, setSavingBroker] = useState(false)

    // Add Webhook Modal
    const [addWebhookOpen, setAddWebhookOpen] = useState(false)
    const [webhookTypes, setWebhookTypes] = useState<WebhookType[]>([])
    const [newWebhook, setNewWebhook] = useState({ webhookTypeId: 0, webhookUrl: '', apiKey: '', isActive: true })
    const [savingWebhook, setSavingWebhook] = useState(false)

    // Edit Webhook Modal
    const [editWebhookOpen, setEditWebhookOpen] = useState(false)
    const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null)
    const [editWebhookData, setEditWebhookData] = useState({ webhookUrl: '', apiKey: '', isActive: true })

    // Delete Webhook Dialog
    const [deleteWebhookOpen, setDeleteWebhookOpen] = useState(false)
    const [deletingWebhook, setDeletingWebhook] = useState<Webhook | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (id) {
            fetchBroker()
        }
    }, [id])

    const fetchBroker = async () => {
        if (!id) return

        try {
            setLoading(true)
            const data = await apiClient.getBrokerById(id)
            setBroker(data)
            setError(null)
        } catch (err) {
            setError('Failed to load broker details')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleEditBroker = () => {
        if (!broker) return
        setEditBrokerData({
            name: broker.name,
            address1: broker.address1 || '',
            address2: broker.address2 || '',
            city: broker.city || '',
            state: broker.state || '',
            postalCode: broker.postalCode || '',
            country: broker.country || '',
            contactFirstName: broker.contactFirstName || '',
            contactLastName: broker.contactLastName || '',
            contactEmail: broker.contactEmail || '',
            contactPhone: broker.contactPhone || '',
            isActive: broker.isActive,
        })
        setEditBrokerOpen(true)
    }

    const handleSaveBroker = async () => {
        if (!broker) return

        try {
            setSavingBroker(true)
            await apiClient.updateBroker(broker.id, editBrokerData)
            setEditBrokerOpen(false)
            await fetchBroker()
        } catch (err) {
            console.error(err)
            setError('Failed to update broker')
        } finally {
            setSavingBroker(false)
        }
    }

    const handleAddWebhook = async () => {
        if (!broker) return

        try {
            const types = await apiClient.getWebhookTypes()
            setWebhookTypes(types)
            setNewWebhook({ webhookTypeId: 0, webhookUrl: '', apiKey: '', isActive: true })
            setAddWebhookOpen(true)
        } catch (err) {
            console.error(err)
            setError('Failed to load webhook types')
        }
    }

    const handleSaveWebhook = async () => {
        if (!broker || !newWebhook.webhookTypeId || !newWebhook.webhookUrl) {
            return
        }

        try {
            setSavingWebhook(true)
            await apiClient.createBrokerWebhook(broker.id, newWebhook)
            setAddWebhookOpen(false)
            await fetchBroker()
        } catch (err) {
            console.error(err)
            setError('Failed to create webhook')
        } finally {
            setSavingWebhook(false)
        }
    }

    const handleEditWebhook = (webhook: Webhook) => {
        setEditingWebhook(webhook)
        setEditWebhookData({
            webhookUrl: webhook.WebhookUrl,
            apiKey: webhook.ApiKey || '',
            isActive: webhook.IsActive,
        })
        setEditWebhookOpen(true)
    }

    const handleUpdateWebhook = async () => {
        if (!editingWebhook) return

        try {
            setSavingWebhook(true)
            await apiClient.updateBrokerWebhook(editingWebhook.Id, editWebhookData)
            setEditWebhookOpen(false)
            await fetchBroker()
        } catch (err) {
            console.error(err)
            setError('Failed to update webhook')
        } finally {
            setSavingWebhook(false)
        }
    }

    const handleDeleteWebhook = (webhook: Webhook) => {
        setDeletingWebhook(webhook)
        setDeleteWebhookOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (!deletingWebhook) return

        try {
            setDeleting(true)
            await apiClient.deleteBrokerWebhook(deletingWebhook.Id)
            setDeleteWebhookOpen(false)
            await fetchBroker()
        } catch (err) {
            console.error(err)
            setError('Failed to delete webhook')
        } finally {
            setDeleting(false)
        }
    }

    // Get available webhook types (those not already configured)
    const getAvailableWebhookTypes = () => {
        if (!broker) return []
        const usedTypeIds = broker.webhooks.map(w => w.WebhookTypeId)
        return webhookTypes.filter(t => !usedTypeIds.includes(t.Id))
    }

    if (loading) {
        return (
            <Container maxWidth="lg">
                <CircularProgress />
            </Container>
        )
    }

    if (error && !broker) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error}</Alert>
            </Container>
        )
    }

    if (!broker) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">Broker not found</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/brokers')} sx={{ mb: 2 }}>
                    Back to Brokers
                </Button>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" component="h1">
                        {broker.name}
                    </Typography>
                    <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditBroker}>
                        Edit Broker
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Broker Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Name
                                    </Typography>
                                    <Typography variant="body1">{broker.name}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Status
                                    </Typography>
                                    <Chip
                                        label={broker.isActive ? 'Active' : 'Inactive'}
                                        color={broker.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Contact Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {broker.contactFirstName || broker.contactLastName
                                            ? `${broker.contactFirstName || ''} ${broker.contactLastName || ''}`.trim()
                                            : 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Contact Email
                                    </Typography>
                                    <Typography variant="body1">{broker.contactEmail || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Contact Phone
                                    </Typography>
                                    <Typography variant="body1">{broker.contactPhone || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Address
                                    </Typography>
                                    <Typography variant="body1">
                                        {[broker.address1, broker.address2, broker.city, broker.state, broker.postalCode, broker.country]
                                            .filter(Boolean)
                                            .join(', ') || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Webhooks</Typography>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddWebhook}>
                                    Add Webhook
                                </Button>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Type</TableCell>
                                            <TableCell>URL</TableCell>
                                            <TableCell>API Key</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {broker.webhooks.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    No webhooks configured
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            broker.webhooks.map(webhook => (
                                                <TableRow key={webhook.Id}>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {webhook.WebhookTypeName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {webhook.WebhookTypeDescription}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{webhook.WebhookUrl}</TableCell>
                                                    <TableCell>{webhook.ApiKey ? '•••••••••' : '-'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={webhook.IsActive ? 'Active' : 'Inactive'}
                                                            color={webhook.IsActive ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleEditWebhook(webhook)}
                                                            title="Edit webhook"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteWebhook(webhook)}
                                                            title="Delete webhook"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Edit Broker Modal */}
            <Dialog open={editBrokerOpen} onClose={() => setEditBrokerOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Edit Broker</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={editBrokerData.name || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, name: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact First Name"
                                value={editBrokerData.contactFirstName || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, contactFirstName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Last Name"
                                value={editBrokerData.contactLastName || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, contactLastName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Email"
                                type="email"
                                value={editBrokerData.contactEmail || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, contactEmail: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Phone"
                                value={editBrokerData.contactPhone || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, contactPhone: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 1"
                                value={editBrokerData.address1 || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, address1: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address Line 2"
                                value={editBrokerData.address2 || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, address2: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="City"
                                value={editBrokerData.city || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, city: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="State"
                                value={editBrokerData.state || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, state: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Postal Code"
                                value={editBrokerData.postalCode || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, postalCode: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Country"
                                value={editBrokerData.country || ''}
                                onChange={e => setEditBrokerData({ ...editBrokerData, country: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editBrokerData.isActive}
                                        onChange={e => setEditBrokerData({ ...editBrokerData, isActive: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditBrokerOpen(false)} disabled={savingBroker}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveBroker} variant="contained" disabled={savingBroker}>
                        {savingBroker ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Webhook Modal */}
            <Dialog open={addWebhookOpen} onClose={() => setAddWebhookOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Webhook</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Webhook Type</InputLabel>
                                <Select
                                    value={newWebhook.webhookTypeId}
                                    onChange={e => setNewWebhook({ ...newWebhook, webhookTypeId: e.target.value as number })}
                                    label="Webhook Type"
                                >
                                    {getAvailableWebhookTypes().map(type => (
                                        <MenuItem key={type.Id} value={type.Id}>
                                            {type.Name} - {type.Description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Webhook URL"
                                value={newWebhook.webhookUrl}
                                onChange={e => setNewWebhook({ ...newWebhook, webhookUrl: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="API Key (Optional)"
                                value={newWebhook.apiKey}
                                onChange={e => setNewWebhook({ ...newWebhook, apiKey: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newWebhook.isActive}
                                        onChange={e => setNewWebhook({ ...newWebhook, isActive: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddWebhookOpen(false)} disabled={savingWebhook}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveWebhook}
                        variant="contained"
                        disabled={savingWebhook || !newWebhook.webhookTypeId || !newWebhook.webhookUrl}
                    >
                        {savingWebhook ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Webhook Modal */}
            <Dialog open={editWebhookOpen} onClose={() => setEditWebhookOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Webhook</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Webhook Type"
                                value={editingWebhook?.WebhookTypeName || ''}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Webhook URL"
                                value={editWebhookData.webhookUrl}
                                onChange={e => setEditWebhookData({ ...editWebhookData, webhookUrl: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="API Key (Optional)"
                                value={editWebhookData.apiKey}
                                onChange={e => setEditWebhookData({ ...editWebhookData, apiKey: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editWebhookData.isActive}
                                        onChange={e => setEditWebhookData({ ...editWebhookData, isActive: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditWebhookOpen(false)} disabled={savingWebhook}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateWebhook}
                        variant="contained"
                        disabled={savingWebhook || !editWebhookData.webhookUrl}
                    >
                        {savingWebhook ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Webhook Dialog */}
            <Dialog open={deleteWebhookOpen} onClose={() => setDeleteWebhookOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete the <strong>{deletingWebhook?.WebhookTypeName}</strong> webhook? This action
                    cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteWebhookOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
